import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Badge, formatTimeDate } from "@";
import { DataTable } from "@/shared/components/tanstackTable";
import { useOrderActivityQuery } from "../utils/use-order-activity";
import {
  actorTypeLabels,
  type OrderEvent,
  orderEventTypeLabels,
} from "../utils/types";
import PayloadDialog from "./PayloadDialog";

interface OrderActivityTableProps {
  orderId?: number | string;
}

export default function OrderActivityTable({
  orderId,
}: OrderActivityTableProps) {
  const [pg, setPg] = useState({ page: 1, size: 10 });
  const [activePayload, setActivePayload] = useState<Record<
    string,
    unknown
  > | null>(null);

  const { data, isLoading } = useOrderActivityQuery({
    orderId,
    page: pg.page,
    pageSize: pg.size,
  });

  const columns = useMemo<ColumnDef<OrderEvent>[]>(
    () => [
      {
        id: "occurredOn",
        header: "التاريخ والوقت",
        accessorFn: (row) => formatTimeDate(row.occurredOn),
        size: 180,
      },
      {
        id: "eventName",
        header: "الإجراء",
        cell: ({ row }) => {
          const { eventType, eventName } = row.original;
          return (
            <Badge
              title={orderEventTypeLabels[eventType] ?? eventName}
              color="default"
            />
          );
        },
        size: 240,
      },
      {
        id: "actor",
        header: "بواسطة",
        accessorFn: (row) =>
          row.actorDisplayName ??
          row.actorRole ??
          actorTypeLabels[row.actorType] ??
          "—",
        size: 160,
      },
      {
        id: "payload",
        header: "التفاصيل",
        size: 200,
        cell: ({ row }) => {
          const { payload } = row.original;
          if (!payload) return <span className="text-gray-400">—</span>;
          return (
            <button
              type="button"
              onClick={() => setActivePayload(payload)}
              className="text-purple-700 hover:underline dark:text-purple-400"
            >
              عرض التفاصيل
            </button>
          );
        },
      },
    ],
    [],
  );

  const pagination = useMemo(
    () => ({
      page: pg.page,
      size: pg.size,
      rows: data?.totalItems ?? 0,
    }),
    [pg.page, pg.size, data?.totalItems],
  );

  if (data?.data.length === 0) {
    return null;
  }
  return (
    <section className="card p-3">
      <h3 className="mb-2 text-gray-600 dark:text-gray-400">سجل النشاط</h3>
      <DataTable
        data={data?.data ?? []}
        columns={columns}
        isLoading={isLoading}
        pagination={pagination}
        onPageChange={(page: number) => setPg((s) => ({ ...s, page }))}
        onPageSizeChange={(size: number) => setPg({ page: 1, size })}
      />
      <PayloadDialog
        payload={activePayload}
        onClose={() => setActivePayload(null)}
      />
    </section>
  );
}
